import { Loading } from '@rematter/ui-web';
import { useAtomValue } from 'jotai';

import { $isGloballyLoading } from '~/shared/state/loading.state';

/**
 * Global loading overlay component
 *
 * Displays a full-screen semi-transparent overlay with a loading spinner
 * when any loading operation is active that should show the overlay.
 *
 * Features:
 * - Fixed position covering entire viewport
 * - Semi-transparent background with subtle blur
 * - Large, centered spinner for visibility (32px)
 * - High z-index to appear above all content
 * - Accessible with ARIA attributes
 * - Automatically controlled by loading state registry
 *
 * @example
 * ```tsx
 * // In app.tsx
 * <ApiProvider>
 *   <GlobalLoadingOverlay />
 *   <AppRouter />
 * </ApiProvider>
 * ```
 */
export const GlobalLoadingOverlay = () => {
  const isLoading = useAtomValue($isGloballyLoading);

  if (!isLoading) return null;

  return (
    <div
      className="@GlobalLoadingOverlay fixed top-0 left-0 z-9999 flex h-screen w-screen items-center justify-center bg-[#00000066]"
      aria-live="polite"
      aria-busy={isLoading}
      role="status"
    >
      <Loading isLoading isGlobal isBoxed showLabel={false} />
    </div>
  );
};
